import {useCallback, VFC, useState, useEffect} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { List, makeStyles, MenuItem, FormControl, InputLabel, Select, TextField } from '@material-ui/core';
import {Timestamp} from 'firebase/firestore'
import { CartListItem } from '../components/Products/CartListItem';
import { PrimaryButton } from '../components/Uikit/PrimaryButton';
import { RootState } from '../reducks/store/store';
import { getProductsInCart } from '../reducks/users/selectors';
import type { ProductInCart } from '../reducks/users/types';
import { orderProduct, resetReceivedDate } from '../reducks/products/operations';
import Container from '@mui/material/Container';
import { resetCart } from '../reducks/users/operations';
import { PrimaryOutlineButton } from '../components/Uikit/PrimaryOutlineButton';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';


const useStyles = makeStyles((theme) => ({
  detailBox: {
    margin: '0 auto',
    [theme.breakpoints.down('sm')]: {
      // スマートフォンサイズ iphoneSEのサイズ
      width: 320,
    },
    [theme.breakpoints.up('sm')]: {
      width: 512,
    },
  },
  orderBox: {
    border: '1px solid #ddd',
    borderRadius: 4,
    boxShadow: '0 4px 1px 1px #ddd',
    height: 120,
    margin: '24px auto 16px auto',
    padding: 16,
    width: 288,
  },
  row: {
    display: 'flex',
    flexFlow: 'row wrap',
    marginBottom: 16,
  },
  label: {
    marginLeft: 0,
    marginRight: 'auto',
    fontSize: '25px',
  },
  value: {
    fontWeight: 600,
    marginRight: 0,
    marginLeft: 'auto',
    fontSize: '25px',
  },
  largeLabel: {
    fontSize: '1.5rem',
  },
}));

const OrderConfirm: VFC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [note, setNote] = useState("");
  const [receiveTime, setReceiveTime] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const selector = useSelector((state: RootState) => state);
  const navigate = useNavigate();
  const productsInCart = getProductsInCart(selector);

  // 小計を計算
  // useMemoでメモ化
  const subtotal = productsInCart.reduce(
    (sum: number, product: ProductInCart) => (sum += product.price * product.quantity),
    0
  );

  // 合計金額を計算
  const total = subtotal;

  const receiveDate = selector.global.receiveDate?.toLocaleDateString();

  const times = [];
  for (let i = 10; i <= 16; i++) {
    times.push(`${i}:00`);
    if (i !== 16) times.push(`${i}:30`);
  }

  /**
   * 注文を確定する.
   */
  const order = useCallback(() => {

    // 受け取り時間が入力されていない際に、トーストを出す。
    if (receiveTime.getHours() === 0 && receiveTime.getMinutes() === 0 && receiveTime.getSeconds() === 0) {
      toast.error('受け取り時間を選択してください。');
      return;
    }
    const receiveDateParts = selector.global.receiveDate ?? new Date();
    const receiveDateYear = receiveDateParts.getFullYear();
    const receiveDateMonth = receiveDateParts.getMonth();
    const receiveDateDay = receiveDateParts.getDate();
    const receiveTimeParts = receiveTime;
    const receiveTimeHours = receiveTimeParts.getHours();
    const receiveTimeMinutes = receiveTimeParts.getMinutes();
    const combinedDateTime = new Date(receiveDateYear, receiveDateMonth, receiveDateDay, receiveTimeHours, receiveTimeMinutes);
    const combinedDateTimeTimestamp = Timestamp.fromDate(combinedDateTime);

    // 注文日時が今日以前の場合、トーストを出す。
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    receiveDateParts.setHours(0, 0, 0, 0);
    if (receiveDateParts <= now) {
      toast.error('受け取り日時が正しくありません。注文を最初からやり直してください。');
      return;
    }
    dispatch(orderProduct(productsInCart, total, note, combinedDateTimeTimestamp, navigate));
    dispatch(resetCart());
    dispatch(resetReceivedDate());
  }, [productsInCart, total, note, receiveTime, dispatch, navigate, selector.global.receiveDate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className='mx-auto my-0 max-w-xl relative py-0 px-4 text-center w-full sm:max-w-5xl'>
      <h2 className='text-pandacco text-2xl mt-0 mx-auto mb-4'>注文の確認</h2>
      <div className=''>
      {/* <div className='flex flex-row flex-wrap'> */}
        <div className={classes.detailBox}>
          <List>
            {productsInCart.length > 0 &&
              (
                !productsInCart.every((product: ProductInCart) => product.quantity === 0) &&
                  (
                    productsInCart.map((product: ProductInCart) => (
                      product.quantity !== 0 ? <CartListItem product={product} key={product.id} /> : null
                    ))
                  )
              )
            }
          </List>
        </div>
        {productsInCart.length > 0 &&
          !productsInCart.every((product: ProductInCart) => product.quantity === 0) &&
          (
            <>
              <div>
                受け取り日
                <p>{receiveDate}</p>
              </div>
              <br />
              <Container
                component="form"
                sx={{
                  width: {
                    xs: '100%',  // For extra small screens, it will take 100% width
                    sm: 320,  // For small screens and up, it will take 320px width
                    md: 512,  // For medium screens and up, it will take 512px width
                  },
                  '& .MuiTextField-root': {
                    my: 3,
                    width: {xs: '80%', sm: '50%'}
                  },
                  mb: 2,
                }}
                noValidate
                autoComplete="off"
              >
                <FormControl fullWidth error={receiveTime.getHours() === 0 && receiveTime.getMinutes() === 0 && receiveTime.getSeconds() === 0}>
                  <InputLabel className={classes.largeLabel}>受け取り時間選択</InputLabel>
                  <Select
                    value={receiveTime.getHours().toString() + ":" + ("00" + receiveTime.getMinutes()).slice(-2)}
                    onChange={(e) => {
                      const [hour, minute] = (e.target.value as string).split(':');
                      const newReceiveTime = new Date(receiveTime);
                      newReceiveTime.setHours(Number(hour), Number(minute), 0);
                      setReceiveTime(newReceiveTime);
                    }}
                  >
                    {times.map((time, index) => (
                      <MenuItem key={index} value={time}>
                        {time}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Container>
            </>
          )
        }
        <Container
          component="form"
          sx={{
            width: '100%',
            '& .MuiTextField-root': {
              my: 3,
              width: {xs: '80%', sm: '50%'}
            },
            '& .css-1kty9di-MuiFormLabel-root-MuiInputLabel-root.Mui-focused':{
              color: '#541414'
            },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottomColor: '#541414',  // ホバー時のボーダー色
            },
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#541414',    // ホバー時のボーダー色(アウトライン)
              },
            mb: 0,
            }
          }}
          noValidate
          autoComplete="off"
        >
          {productsInCart.length > 0 && (
            !productsInCart.every((product: ProductInCart) => product.quantity === 0) ?
            (
              <>
                <div className="text-left mt-12">
                  <p>※ カスタマイズを希望される方はこちらに詳細をご記入ください。</p>
                </div>
                <TextField
                  id="outlined-multiline-static"
                  label="備考欄"
                  multiline
                  minRows={4}
                  value={note}
                  variant='outlined'
                  onChange={(event) => setNote(event.target.value)}
                  style = {{ marginTop: 5 }}
                />
              </>
            ) : (
              <>
                <p className='text-gray-500 text-xl'>カート内に商品が入っていません</p>
                <div className='h-8 sm:h-12' />
              </>
            )
            )
          }
          <Container
            sx = {{
              width: {xs: '80%', sm: '50%'}
            }}
          >
          <div className={classes.row}>
            <div className={classes.label}>{'合計(税込)'}</div>
            <div className={classes.value}>{`¥${subtotal}`}</div>
          </div>
          </Container>
          {productsInCart.length > 0 &&
              (
                !productsInCart.every((product: ProductInCart) => product.quantity === 0) &&
                  ( <PrimaryButton label={'注文する'} onClick={order} />)
              )
          }
          <div className='h-3 sm:h-4' />
          <PrimaryOutlineButton label={'注文を続ける'} onClick={() =>  navigate('/')} />
        </Container>
      </div>
    </section>
  );
};
export default OrderConfirm;
